import type { MasterResult } from '@/lib/master-engine';

export interface MasterRecommendationData {
  companyName: string;
  monthlyLoss: number;
  annualPotential: number;
  dealsPerRepPerMonth: number;
  blockingStages: number;
  dominantBottleneck: {
    title: string;
    description: string;
    managementExplanation: string;
    monthlyLoss: number;
    dealsPerRep: number;
    blockingStages: number;
  };
  revenueImpactMap: Array<{
    rank: number;
    lever: string;
    targetMetric: string;
    monthlyImpact: number;
    annualImpact: number;
    timeToSignal: string;
  }>;
  dataQualityScore: number;
  dataQualityLevel: 'high' | 'medium' | 'low';
  missingFields: string[];
  steuerungsrisiko: string;
  topPriorityTitle: string;
}

function safeNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function buildRecommendationMasterData(result: MasterResult): MasterRecommendationData {
  const team = result.teamPerformance;
  const metrics = result.computedMetrics;
  const goalPath = metrics?.goalPath;
  const costIneff = result.computedMetrics?.costInefficiency;
  const topLevers = Array.isArray(result.topLevers) ? result.topLevers : [];

  // Revenue gap
  const annualGap =
    safeNum(team?.projectedYearEndGap) > 0
      ? safeNum(team?.projectedYearEndGap)
      : safeNum(metrics?.annualRevenueGap) || safeNum(result.views?.hero?.annualRevenueGap);
  const monthlyLoss =
    annualGap > 0
      ? Math.round(annualGap / 12)
      : safeNum(metrics?.monthlyRevenueGap) || safeNum(result.views?.hero?.monthlyRevenueGap);

  const annualPotential =
    safeNum(costIneff?.revenueEfficiencyGapAnnual) > 0
      ? safeNum(costIneff?.revenueEfficiencyGapAnnual)
      : annualGap;

  // Deals per rep per month
  const reps = Math.max(1, safeNum(team?.productiveReps) || 1);
  const dealsPerRepPerMonth = safeNum(goalPath?.requiredDealsPerMonthFromNow) > 0
    ? Math.ceil(safeNum(goalPath.requiredDealsPerMonthFromNow) / reps)
    : 0;

  // Blocking stages (from funnelDiagnosis if available)
  const funnelDiag = (result as any).funnelDiagnosis as Record<string, { isBlocking?: boolean }> | undefined;
  const blockingStages = funnelDiag
    ? Object.values(funnelDiag).filter((s) => s?.isBlocking).length
    : 0;

  // Dominant bottleneck
  const weakestDim = result.diagnosticScoring?.weakestDimension;
  const priorityDrivers = result.diagnosticScoring?.priorityDrivers || [];
  const primaryDriver = priorityDrivers[0];

  const bottleneckTitle =
    primaryDriver?.title || topLevers[0]?.title || 'Lead-zu-Erstgespräch-Übergang';
  const bottleneckDesc =
    primaryDriver?.summary ||
    topLevers[0]?.whyItMatters ||
    'Der kritischste Engpass liegt im Übergang von Interesse zu qualifiziertem Gespräch.';

  const mgmtExplanation =
    weakestDim === 'REP_PERFORMANCE'
      ? 'Leistungsstreuung zwischen Reps verursacht messbaren Umsatzverlust. Nicht der Markt, nicht das Produkt – sondern fehlende Standards und Steuerung.'
      : weakestDim === 'PROCESS'
      ? 'Fehlende Ownership und Prozessstandards machen den Vertrieb fragil. Jeder Rep arbeitet anders – das kostet täglich Umsatz.'
      : weakestDim === 'SALES_QUALITY'
      ? 'Discovery und Qualifizierung sind nicht standardisiert genug. Angebote gehen zu früh raus, Abschlusswahrscheinlichkeit sinkt.'
      : 'Der Hauptengpass liegt im oberen Funnel: Leads werden zu langsam oder zu unstrukturiert in qualifizierte Gespräche überführt.';

  // Revenue Impact Map
  const revenueImpactMap = topLevers.slice(0, 3).map((lever, idx) => ({
    rank: idx + 1,
    lever: lever.title || `Hebel ${idx + 1}`,
    targetMetric:
      idx === 0
        ? 'Kontakt-zu-Erstgespräch-Rate'
        : idx === 1
        ? 'Discovery-zu-Angebot-Rate'
        : 'Angebot-zu-Abschluss-Rate',
    monthlyImpact: safeNum(lever.monthlyImpact),
    annualImpact: safeNum(lever.annualImpact),
    timeToSignal: idx === 0 ? '2–4 Wochen' : idx === 1 ? '4–6 Wochen' : '6–8 Wochen',
  }));

  // Data quality
  const dqRaw = (result as any).dataQuality as
    | { score?: number; level?: string; missingFields?: string[] }
    | undefined;
  const dataQualityScore = safeNum(dqRaw?.score) || 70;
  const rawLevel = dqRaw?.level || 'medium';
  const dataQualityLevel: 'high' | 'medium' | 'low' =
    rawLevel === 'high' || rawLevel === 'medium' || rawLevel === 'low' ? rawLevel : 'medium';
  const missingFields = Array.isArray(dqRaw?.missingFields) ? dqRaw!.missingFields : [];

  const steuerungsrisiko =
    dataQualityScore < 40
      ? 'Kritisch – fehlende Daten verhindern saubere Steuerung und Prognosen.'
      : dataQualityScore < 70
      ? 'Mittel – wichtige Datenpunkte fehlen, was Forecast und Priorisierung einschränkt.'
      : 'Niedrig – Datenbasis ist solide genug für operative Steuerung.';

  const topPriorityTitle = topLevers[0]?.title || 'Lead-zu-Erstgespräch stabilisieren';

  return {
    companyName:
      result.normalizedData?.companyName || (result as any).companyName || 'Ihr Unternehmen',
    monthlyLoss,
    annualPotential,
    dealsPerRepPerMonth,
    blockingStages,
    dominantBottleneck: {
      title: bottleneckTitle,
      description: bottleneckDesc,
      managementExplanation: mgmtExplanation,
      monthlyLoss,
      dealsPerRep: dealsPerRepPerMonth,
      blockingStages,
    },
    revenueImpactMap,
    dataQualityScore,
    dataQualityLevel,
    missingFields,
    steuerungsrisiko,
    topPriorityTitle,
  };
}
